import {fadeIn, fadeOutFadeIn} from "./Animate.ts";
import {$} from "./Dom.ts";
import {loadPanelContent} from "./Onglets.ts";


const TabSystemTypedEventTarget = EventTarget as {new(): TabSystemEventTarget; prototype: TabSystemEventTarget};

export class TabSystem extends TabSystemTypedEventTarget {
    private readonly options: TabOptions = {
        tabs_selector: '.fr-sidemenu__link[role="tab"]',
    }
    private readonly tabs: NodeListOf<HTMLAnchorElement>;
    private activeTab: HTMLAnchorElement | null = null;
    private activePanel: HTMLElement | null = null;

    constructor(options?: TabOptions) {
        super()
        this.options = {...this.options, ...options}
        // Récupérer les onglets et leurs panneaux correspondants
        this.tabs = document.querySelectorAll(this.options.tabs_selector);

        // Associer les événements de clic
        this.init();
    }

    private init() {
        this.tabs.forEach((tab: HTMLAnchorElement) => {
            tab.addEventListener('click', (event) => this.handleTabClick(event, tab));
        });

        // Vérifier si un hash correspondant existe dans l'URL
        const hash = window.location.hash.slice(1); // Récupérer le hash sans le '#'
        if (hash) {
            const tabToActivate = Array.from(this.tabs).find((tab) => tab.getAttribute('aria-controls') === hash);
            const panelToActivate = $<HTMLElement>(`#${hash}`);

            if (tabToActivate && panelToActivate) {
                this.activateTab(tabToActivate); // Activer l'onglet et son panneau correspondant
                return;
            }
        }

        // Si aucun hash ou hash invalide, activer le premier onglet par défaut
        const defaultTab = this.tabs[0];
        if (defaultTab) {
            this.activateTab(defaultTab);
        }
    }

    private handleTabClick(event: Event, tab: HTMLAnchorElement) {
        event.preventDefault();
        this.activateTab(tab);

        // Mettre à jour le hash de l'URL quand un onglet est activé
        const panelId = tab.getAttribute('aria-controls');
        if (panelId) {
            window.history.replaceState(null, '', `#${panelId}`);
        }
    }

    private activateTab(tab: HTMLAnchorElement) {
        if (this.activeTab === tab) {
            // Si déjà actif, ne rien faire
            return;
        }

        // Désactiver l'ancien onglet actif
        if (this.activeTab) {
            const previousParent = this.activeTab.closest('.fr-sidemenu__item');
            if (previousParent) {
                previousParent.classList.remove('fr-sidemenu__item--active');
            }
            this.activeTab.setAttribute('aria-selected', 'false');
            this.activeTab.removeAttribute('aria-current'); // Supprimer aria-current de l'ancien onglet
        }

        // Activer le nouvel onglet
        const newParent = tab.closest('.fr-sidemenu__item');
        if (newParent) {
            newParent.classList.add('fr-sidemenu__item--active');
        }
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('aria-current', 'page');
        this.activeTab = tab;

        // Gérer les panneaux de contenu
        const panelId = tab.getAttribute('aria-controls');
        if (panelId) {
            const newPanel = $<HTMLElement>(`#${panelId}`);
            if (newPanel) {
                // Charger le contenu via AJAX si nécessaire
                loadPanelContent(tab, newPanel).then(() => {
                    this.triggerEvent({target: newPanel, source: tab});
                }).catch((err) => {
                    if(window.Openscop.debug) console.warn(`Failed to load panel content for ${newPanel.id}:`, err)
                }).finally(() => {
                    this.animatePanels(this.activePanel, newPanel);
                    this.activePanel = newPanel;
                })
            }
        }
    }

    private triggerEvent(eventDetail: panelLoaded) {
        const event = new CustomEvent('panel-loaded', {
            detail: eventDetail,
        });
        this.dispatchEvent(event);
    }

    private animatePanels(currentPanel: HTMLElement | null, nextPanel: HTMLElement) {
        if (!nextPanel) {
            if(window.Openscop.debug) console.warn('Next panel is null, animation skipped.');
            return;
        }

        if (!currentPanel) {
            // Cas initial : aucun panneau actif, afficher directement le premier panneau avec fadeIn
            fadeIn(nextPanel, '100ms')
                .then(() => {
                    this.activePanel = nextPanel;
                })
                .catch(err => {
                    if(window.Openscop.debug) console.error('Animation error during initial fadeIn:', err);
                });
        } else {
            // Animation normale : bascule entre deux panneaux (faeOut → fadeIn)
            fadeOutFadeIn(currentPanel, nextPanel)
                .then(() => {
                    this.activePanel = nextPanel; // Mets à jour le panneau actif
                })
                .catch(err => {
                    if(window.Openscop.debug) console.error('Animation error:', err);
                });
        }
    }
}